import { useMemo } from "react";
import Waveform from "@/components/Waveform";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, ShieldCheck } from "lucide-react";

export function WaveformRow({ label, color, kind, rateRef, amplitudeRef }) {
  return (
    <div className="bg-black border border-[#222] rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1 border-b border-[#1a1a1a]">
        <span
          className="text-[10px] uppercase tracking-widest font-mono-clinical font-bold"
          style={{ color }}
        >
          {label}
        </span>
        <span className="text-[10px] text-slate-500 font-mono-clinical">
          25 mm/s
        </span>
      </div>
      <Waveform
        kind={kind}
        color={color}
        height={120}
        rateRef={rateRef}
        amplitudeRef={amplitudeRef}
      />
    </div>
  );
}

export function Readout({ label, value, unit, color, alarm, Icon, testid }) {
  const text = String(value);
  // Scale the font so 3–4 character readouts (e.g. "100", "37.8") stay inside the box.
  const fontSize = text.length >= 4 ? "1.875rem" : text.length === 3 ? "2.125rem" : "2.5rem";
  return (
    <div
      className={`relative bg-[#0a0a0a] border-2 rounded-md p-3 ${
        alarm ? "alarm-border" : "border-[#222]"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3 h-3" style={{ color }} />
          <span
            className="text-[10px] uppercase tracking-widest font-mono-clinical font-bold"
            style={{ color }}
          >
            {label}
          </span>
        </div>
        <span className="text-[9px] text-slate-500 font-mono-clinical">{unit}</span>
      </div>
      <div
        className={`font-mono-clinical font-bold leading-none tabular-nums tracking-tight whitespace-nowrap overflow-hidden ${
          alarm ? "alarm-pulse" : ""
        }`}
        style={{ color, fontSize }}
      >
        {value}
      </div>
    </div>
  );
}

export function ControlSlider({ label, value, onChange, min, max, step, unit, testid, accent, hint }) {
  const sliderValue = useMemo(() => [value], [value]);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-sm font-medium text-slate-700">{label}</Label>
        <div
          className="font-mono-clinical font-bold text-lg"
          style={{ color: accent }}
        >
          {Number(value).toFixed(step < 1 ? 1 : 0)} <span className="text-xs text-slate-500">{unit}</span>
        </div>
      </div>
      <Slider
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function ManualEntry({ label, value, onCommit, testid, step = 1 }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </Label>
      <Input
        type="number"
        defaultValue={value}
        step={step}
        onBlur={(e) => {
          const n = parseFloat(e.target.value);
          if (!isNaN(n)) onCommit(n);
        }}
        data-testid={testid}
        className="font-mono-clinical text-sm h-8"
      />
    </div>
  );
}

export function InterventionBtn({ Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="border-2 border-slate-200 hover:border-[#0047AB] hover:bg-[#0047AB]/5 rounded-md px-3 py-3 text-left transition-all group"
    >
      <Icon className="w-4 h-4 text-slate-600 group-hover:text-[#0047AB] mb-1.5" />
      <div className="text-xs font-semibold text-slate-800 leading-tight">{label}</div>
    </button>
  );
}

// ---------- Trend strip (last 5 min) ----------
function Sparkline({ data, color, min, max }) {
  const W = 300;
  const H = 44;
  if (!data || data.length < 2) {
    return (
      <div className="h-11 flex items-center justify-center rounded bg-black border border-[#1a1a1a] text-[10px] text-slate-600 font-mono-clinical">
        collecting trend…
      </div>
    );
  }
  const clampY = (y) => Math.max(2, Math.min(H - 2, y));
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * W;
      const y = H - ((v - min) / (max - min)) * H;
      return `${x.toFixed(1)},${clampY(y).toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-11 rounded bg-black border border-[#1a1a1a]"
    >
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

const TREND_TESTID_MAP = { HR: "trend-hr", MAP: "trend-map", "SpO₂": "trend-spo2", RR: "trend-rr" };

function TrendRow({ label, unit, color, data, min, max, current }) {
  return (
    <div className="mb-3 last:mb-0" data-testid={TREND_TESTID_MAP[label] || `trend-${label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
      <div className="flex items-center justify-between mb-1">
        <span
          className="text-[10px] uppercase tracking-widest font-mono-clinical font-bold"
          style={{ color }}
        >
          {label}
        </span>
        <span className="font-mono-clinical font-bold text-base" style={{ color }}>
          {current}
          <span className="text-[10px] text-slate-500 ml-1">{unit}</span>
        </span>
      </div>
      <Sparkline data={data} color={color} min={min} max={max} />
    </div>
  );
}

export function TrendStrip({ history, vitals }) {
  return (
    <div
      className="rounded-lg border-2 border-[#222] bg-[#050505] p-4 shadow-2xl"
      data-testid="trend-strip"
    >
      <div className="flex items-center gap-1.5 mb-3">
        <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-clinical">
          Trend · last 5 min
        </span>
      </div>
      <TrendRow
        label="HR"
        unit="bpm"
        color="#39FF14"
        data={history.map((h) => h.hr)}
        min={20}
        max={220}
        current={Math.round(vitals.hr)}
      />
      <TrendRow
        label="MAP"
        unit="mmHg"
        color="#FF8C42"
        data={history.map((h) => h.map).filter((v) => v != null)}
        min={20}
        max={160}
        current={Math.round(vitals.map)}
      />
      <TrendRow
        label="SpO₂"
        unit="%"
        color="#00FFFF"
        data={history.map((h) => h.spo2)}
        min={60}
        max={100}
        current={Math.round(vitals.spo2)}
      />
      <TrendRow
        label="RR"
        unit="/min"
        color="#FFEA00"
        data={history.map((h) => h.rr).filter((v) => v != null)}
        min={0}
        max={60}
        current={Math.round(vitals.rr)}
      />
    </div>
  );
}

// ---------- Quick-reference normal ranges ----------
function RangeRow({ label, unit, range, value, decimals = 0 }) {
  const inRange = value >= range[0] && value <= range[1];
  return (
    <div
      className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0"
      data-testid={`range-${label.toLowerCase()}`}
    >
      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-mono-clinical">
        {label}
      </span>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-slate-500 font-mono-clinical">
          {range[0]}–{range[1]} {unit}
        </span>
        <span
          className="text-sm font-mono-clinical font-bold w-12 text-right"
          style={{ color: inRange ? "#39FF14" : "#FF3333" }}
        >
          {value.toFixed(decimals)}
        </span>
      </div>
    </div>
  );
}

export function NormalRangesCard({ vitals, range, species }) {
  return (
    <div
      className="rounded-lg border-2 border-[#222] bg-[#0a0a0a] p-4 shadow-2xl"
      data-testid="normal-ranges"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono-clinical">
          Reference ranges · {species === "cat" ? "feline" : "canine"}
        </span>
      </div>
      <RangeRow label="HR" unit="bpm" range={range.hr} value={vitals.hr} />
      <RangeRow label="RR" unit="/min" range={range.rr} value={vitals.rr} />
      <RangeRow label="SpO₂" unit="%" range={range.spo2} value={vitals.spo2} />
      <RangeRow label="Temp" unit="°C" range={range.temp} value={vitals.temp} decimals={1} />
      <RangeRow label="MAP" unit="mmHg" range={range.map} value={vitals.map} />
      <p className="text-[10px] text-slate-600 mt-2 leading-relaxed">
        Live values turn red when outside the normal range for this species.
      </p>
    </div>
  );
}
